import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { CITY_PAGES } from "@/data/cityPages";
import { useSeo, SITE_URL } from "@/lib/seo";
import { api } from "@/lib/api";
import { QuoteWizard } from "@/components/QuoteWizard";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Lightbulb, CheckCircle2, MapPin, ShieldCheck, Star } from "lucide-react";

const buildFaqs = (p) => [
  { q: `How do I find a trusted ${p.svc.noun} in ${p.city}?`, a: `Request a free quote on InspectorProLeads. We match your ${p.city} request with verified local ${p.svc.plural} and email them instantly. The first available pro claims it and contacts you directly.` },
  ...p.svc.faqs,
  { q: `What areas near ${p.city} do you cover?`, a: `We cover ${p.city} and all of ${p.info.county}, including ${p.info.neighborhoods.slice(0, 4).join(", ")}, plus cities within about 60 miles of Sacramento.` },
  { q: "Is it free to request a quote?", a: "Yes. Requesting a quote is always free for homeowners, with no obligation to hire." },
];

const buildLd = (p, faqs) => [
  { "@context": "https://schema.org", "@type": "Service", name: p.title, serviceType: p.service,
    areaServed: { "@type": "City", name: p.city, containedInPlace: { "@type": "AdministrativeArea", name: `${p.info.county}, California` } },
    provider: { "@type": "Organization", name: "InspectorProLeads", url: SITE_URL }, url: `${SITE_URL}/services/${p.slug}` },
  { "@context": "https://schema.org", "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })) },
  { "@context": "https://schema.org", "@type": "BreadcrumbList", itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL },
    { "@type": "ListItem", position: 2, name: "Service Areas", item: `${SITE_URL}/services` },
    { "@type": "ListItem", position: 3, name: p.title, item: `${SITE_URL}/services/${p.slug}` }] },
];

const Checks = ({ items }) => (
  <ul className="mt-4 grid sm:grid-cols-2 gap-2">{items.map((t) => (
    <li key={t} className="flex gap-2 text-[color:var(--ink-2)] text-sm"><CheckCircle2 className="w-5 h-5 text-[color:var(--emerald)] shrink-0" /> <span>{t}</span></li>))}</ul>
);

const LocalPros = ({ p }) => {
  const [pros, setPros] = useState([]);
  useEffect(() => { api.get("/businesses", { params: { category: p.trade, city: p.city } }).then(({ data }) => setPros(data.slice(0, 4))).catch(() => {}); }, [p]);
  if (!pros.length) return null;
  return (
    <section className="mt-12" data-testid="city-local-pros">
      <h2 className="font-display text-2xl font-bold text-[color:var(--navy)]">Verified {p.svc.plural} serving {p.city}</h2>
      <div className="grid sm:grid-cols-2 gap-4 mt-4">{pros.map((b) => (
        <div key={b.id} className="card-soft p-5" data-testid={`city-pro-${b.id}`}>
          <div className="font-semibold text-[color:var(--navy)]">{b.name}</div>
          <div className="text-xs text-[color:var(--ink-3)] flex items-center gap-2 mt-1"><MapPin className="w-3 h-3" /> {b.city}
            {b.review_count > 0 && <span className="flex items-center gap-1"><Star className="w-3 h-3 fill-amber-400 text-amber-400" /> {b.rating} ({b.review_count})</span>}</div>
        </div>))}</div>
    </section>
  );
};

const Related = ({ p }) => {
  const sameCity = CITY_PAGES.filter((x) => x.city === p.city && x.slug !== p.slug);
  const sameSvc = CITY_PAGES.filter((x) => x.trade === p.trade && x.city !== p.city).slice(0, 6);
  return (
    <section className="mt-12" data-testid="city-related-links">
      <h2 className="font-display text-xl font-bold text-[color:var(--navy)]">Related services</h2>
      <div className="flex flex-wrap gap-2 mt-4">{[...sameCity, ...sameSvc].map((x) => (
        <Link key={x.slug} to={`/services/${x.slug}`} data-testid={`related-${x.slug}`} className="text-sm px-4 py-2 rounded-full bg-[color:var(--bg-3)] text-[color:var(--ink-2)] hover:text-[color:var(--emerald)] transition-colors">{x.title.replace(", CA", "")}</Link>))}</div>
    </section>
  );
};

function Page({ p }) {
  const nav = useNavigate();
  const faqs = useMemo(() => buildFaqs(p), [p]);
  const ld = useMemo(() => buildLd(p, faqs), [p, faqs]);
  useSeo({
    title: `${p.title} | Free Quotes from Verified Local Pros | InspectorProLeads`,
    description: `Need a ${p.svc.noun} in ${p.city}? Get free quotes from verified local ${p.svc.plural} in ${p.info.county}. Fast, free, no obligation.`,
    path: `/services/${p.slug}`, jsonLd: ld,
  });
  return (
    <div className="container-x py-14" data-testid="city-service-page">
      <nav className="text-sm text-[color:var(--ink-3)]" aria-label="Breadcrumb"><Link to="/" className="hover:text-[color:var(--emerald)]">Home</Link> / <Link to="/services" className="hover:text-[color:var(--emerald)]" data-testid="city-breadcrumb-services">Service Areas</Link> / <span>{p.title}</span></nav>
      <div className="grid lg:grid-cols-[1fr_440px] gap-10 mt-6">
        <div className="min-w-0">
          <span className="inline-flex items-center gap-1 text-xs font-mono uppercase tracking-wider badge-pest px-3 py-1 rounded-full"><MapPin className="w-3 h-3" /> {p.city} · {p.info.county}</span>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold text-[color:var(--navy)] mt-3 leading-tight" data-testid="city-page-title">{p.title}</h1>
          <p className="text-lg text-[color:var(--ink-2)] mt-4">Free quotes from verified local {p.svc.plural} in {p.city}. Tell us what you need, and matched pros reach out fast.</p>
          <Button onClick={() => document.getElementById("city-quote-form")?.scrollIntoView({ behavior: "smooth" })}
            className="btn-amber rounded-full font-semibold mt-5 lg:hidden" data-testid="city-jump-quote-button">Get free quotes</Button>

          <div className="card-soft p-6 mt-8 border-l-4 border-l-[color:var(--emerald)]" data-testid="city-quick-answer">
            <div className="flex items-center gap-2 font-semibold text-[color:var(--navy)]"><Lightbulb className="w-5 h-5 text-amber-500" /> Quick answer</div>
            <p className="text-[color:var(--ink-2)] mt-2 leading-relaxed">To hire a {p.svc.noun} in {p.city}, {p.info.state}, submit a free request on InspectorProLeads. We instantly match it with verified {p.svc.plural} serving {p.city} and {p.info.county}, and the first available pro contacts you directly. It takes about 60 seconds, with no obligation.</p>
          </div>

          <section className="mt-10">
            <h2 className="font-display text-2xl font-bold text-[color:var(--navy)]">What {p.city} homeowners should know</h2>
            {p.local.map((t, i) => <p key={i} className="text-[color:var(--ink-2)] leading-relaxed mt-4">{t}</p>)}
            <p className="text-[color:var(--ink-2)] leading-relaxed mt-4">{p.info.housing} {p.info.climate}</p>
          </section>

          <section className="mt-10">
            <h2 className="font-display text-2xl font-bold text-[color:var(--navy)]">What's included</h2>
            <Checks items={p.svc.what} />
          </section>

          <section className="mt-10">
            <h2 className="font-display text-2xl font-bold text-[color:var(--navy)]">How to choose a {p.svc.noun} in {p.city}</h2>
            <Checks items={p.svc.tips} />
          </section>

          <section className="mt-10" data-testid="city-neighborhoods">
            <h2 className="font-display text-2xl font-bold text-[color:var(--navy)]">{p.city} neighborhoods we serve</h2>
            <div className="flex flex-wrap gap-2 mt-4">{p.info.neighborhoods.map((n) => <span key={n} className="text-sm px-3 py-1.5 rounded-full border border-slate-200 text-[color:var(--ink-2)]">{n}</span>)}</div>
          </section>

          <LocalPros p={p} />

          <section className="mt-12" data-testid="city-faq">
            <h2 className="font-display text-2xl font-bold text-[color:var(--navy)]">Frequently asked questions</h2>
            <Accordion type="multiple" className="mt-4">
              {faqs.map((f, i) => (
                <AccordionItem key={f.q} value={`f${i}`} data-testid={`city-faq-${i}`}>
                  <AccordionTrigger className="text-left font-semibold text-[color:var(--navy)]">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-[color:var(--ink-2)] leading-relaxed">{f.a}</AccordionContent>
                </AccordionItem>))}
            </Accordion>
          </section>

          <Related p={p} />

          <div className="mt-12 rounded-3xl bg-[color:var(--navy)] text-white p-8 flex flex-col sm:flex-row items-center justify-between gap-5" data-testid="city-pro-cta">
            <div><div className="font-display text-xl font-bold">Are you a {p.svc.noun} in {p.city}?</div>
              <p className="text-slate-300 text-sm mt-1">Get exclusive local leads. 60 days free, then $15/month.</p></div>
            <Button onClick={() => nav("/pricing")} className="btn-amber rounded-full font-semibold shrink-0" data-testid="city-pro-cta-button">Join as a pro</Button>
          </div>
        </div>

        <aside id="city-quote-form" className="lg:sticky lg:top-24 h-fit card-soft p-6 scroll-mt-24" data-testid="city-quote-form">
          <div className="flex items-center gap-2 text-sm font-semibold text-[color:var(--emerald)] mb-2"><ShieldCheck className="w-4 h-4" /> Free · No obligation</div>
          <h2 className="font-display text-xl font-bold text-[color:var(--navy)] mb-4">Get {p.service} quotes in {p.city}</h2>
          <QuoteWizard key={p.slug} presetService={p.trade} presetCity={p.city} />
        </aside>
      </div>
    </div>
  );
}

function NotFound() {
  useSeo({ title: "Page not found | InspectorProLeads", description: "This service page doesn't exist.", path: "/services", jsonLd: {} });
  useEffect(() => {
    const m = document.createElement("meta"); m.name = "robots"; m.content = "noindex"; document.head.appendChild(m);
    return () => m.remove();
  }, []);
  return (
    <div className="container-x py-24 text-center" data-testid="city-not-found">
      <h1 className="font-display text-3xl font-bold text-[color:var(--navy)]">Page not found</h1>
      <Link to="/services" className="text-[color:var(--emerald)] mt-4 inline-block">See all service areas</Link>
    </div>
  );
}

export default function CityServicePage() {
  const { slug } = useParams();
  const p = CITY_PAGES.find((x) => x.slug === slug);
  if (!p) return <NotFound />;
  return <Page p={p} />;
}
